/* eslint-disable react/display-name */
import { Flex } from '@chakra-ui/react'
import * as Sentry from '@sentry/react'
import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import universal from 'react-universal-component'

import { Search } from '~component/Search/Search'
import { routesToFlagsMap } from '~redux/routes'
import {
  CustomerSelector,
  FullScreenLoaderRound,
  Pill,
} from '~shared-components/components'
import { ConfirmLogoutModal } from '~shared-components/components/ConfirmLogoutModal/ConfirmLogoutModal'
import { ErrorBoundary } from '~shared-components/components/ErrorBoundary/ErrorBoundary'
import {
  EXTERNAL_USER_ADMIN_PER_COUNTRY,
  FROZEN_FLAG_VALUES,
  GLOBAL_SEARCH_PER_COUNTRY,
  INVOICES_PAGE_PER_COUNTRY,
  QUOTES_PAGE_PER_COUNTRY,
} from '~shared-components/components/FlagRender/frozenFlags'
import { Footer } from '~shared-components/components/Footer/Footer'
import { Header } from '~shared-components/components/Header/Header'
import { CartModalsController } from '~shared-components/components/Modal'
import { NavigationModal } from '~shared-components/components/Modal/NavigationModal'
import { RouterLink } from '~shared-components/components/RouterLink/RouterLink'
import { Toaster } from '~shared-components/components/Toaster/Toaster'
import {
  AppEnvironment,
  getEnvironmentTagColor,
} from '~shared-components/config'
import { switchContextThunk } from '~shared-components/filters/filter.actions'
import useAuthentication from '~shared-components/hooks/useAuthentication'
import { useCart } from '~shared-components/hooks/useCart'
import { useReloadIfLoggedOut } from '~shared-components/hooks/useReloadIfLoggedOut'
import { useSelectContext } from '~shared-components/hooks/useSelectContext'
import { useSyncRoutes } from '~shared-components/hooks/useSyncRoutes'
import { useToaster } from '~shared-components/hooks/useToaster'
import { useTranslations } from '~shared-components/hooks/useTranslations'
import { CartSelector } from '~shared-components/redux/selectors/cart.selector'
import { ConfigSelector } from '~shared-components/redux/selectors/config.selector'
import { LocationSelector } from '~shared-components/redux/selectors/location.selector'
import { NavigationSelector } from '~shared-components/redux/selectors/navigation.selector'
import { LayoutPadding } from '~shared-components/theme'
import { CustomerContext } from '~shared-components/transforms/user.transforms'
import { TranslationKey } from '~shared-components/translations/default'
import { Page } from '~shared-components/utils/enums'

import { UniversalComponentLoaderProps } from './page'
import NotFound from './page/NotFound'
import { isAllowedToSeePage } from './permissions'

// Note: in the webpack config it limits the path to only 2 options: page or pages-v2
// this is done to prevent webpack from rendering chunks for each file within the shared folder
const load = ({ page, path }: UniversalComponentLoaderProps) =>
  import(/* webpackChunkName: '[request]' */ `./${path}/${page}`)

const Loading = React.memo(() => (
  <LayoutPadding>
    <FullScreenLoaderRound />
  </LayoutPadding>
))

const UniversalComponent = universal(load, {
  chunkName: ({ page }) => page,
  error: () => <NotFound />,
  loading: Loading,
  render: ({ country }, Module) =>
    Module ? <Module country={country} /> : <></>,
  resolve: ({ page, path }) => require.resolveWeak(`./${path}/${page}`),
})

const contextSwitchRedirects = [
  {
    from: Page.OrderDetails,
    to: Page.Orders,
  },
  {
    from: Page.QuoteDetails,
    to: Page.Quotes,
  },
  {
    from: Page.ProductDetails,
    to: Page.Products,
  },
]

const App = () => {
  const [confirmLogoutModalMessage, setConfirmLogoutModalMessage] = useState<
    string | React.ReactNode
  >('')

  const t = useTranslations()
  const dispatch = useDispatch()
  const page = useSelector(LocationSelector.pageType)
  const orderDetailsNavState = useSelector(
    NavigationSelector.navState(Page.OrderDetails),
  )
  const cartProducts = useSelector(CartSelector.products)
  const environment = useSelector(ConfigSelector.environment)
  const country = useSelector(ConfigSelector.localeCountry)

  const { setSelectContext } = useSelectContext()

  const { removeToast } = useToaster()
  // Redirect to login when one of the tabs is logged out
  useReloadIfLoggedOut()

  const { onLogout, user } = useAuthentication()

  useSyncRoutes(country, FROZEN_FLAG_VALUES, routesToFlagsMap)

  useCart()

  const isSelectorRequired = () =>
    ![
      Page.AccessRestricted,
      Page.NotFound,
      Page.PaymentInvoice,
      Page.TermsAndConditions,
      Page.Users,
      Page.UserEdit,
    ].includes(page as Page)

  const isHeaderRequired = () => ![Page.PaymentInvoice].includes(page as Page)

  const onChangeCustomerContext = async (customerContext: CustomerContext) => {
    user!.currentCustomerContextId = customerContext.customerContextId
    const redirect = contextSwitchRedirects.find(r => page === r.from)

    await setSelectContext(
      customerContext,
      customerContext.shipToAddresses[0].id,
    )

    dispatch(switchContextThunk({ redirectTo: redirect?.to }))
  }

  return (
    <>
      {isHeaderRequired() && (
        <>
          {isSelectorRequired() && (
            <CustomerSelector
              customers={user?.customers}
              onChange={onChangeCustomerContext}
            />
          )}
          <Header
            cartLink={{ type: Page.Cart }}
            environmentTag={
              environment !== AppEnvironment.PRODUCTION ? (
                <Pill
                  bgColor={getEnvironmentTagColor(environment)}
                  variant="chip-solid"
                >
                  {`${environment} - ` + process.env.BC_VERSION}
                </Pill>
              ) : undefined
            }
            homeLink={{ type: Page.Orders }}
            isLoggedIn={Boolean(user?.email)}
            onLogout={() => {
              if (cartProducts.length > 0) {
                setConfirmLogoutModalMessage(
                  t('logout.itemsinyourshoppingcartcanbelost'),
                )
              } else if (
                page === Page.OrderDetails &&
                Boolean(orderDetailsNavState?.isDirty)
              ) {
                setConfirmLogoutModalMessage(t('logout.changeswillbelost'))
              } else {
                onLogout()
              }
            }}
            profileLink={{ type: Page.Profile }}
            search={
              GLOBAL_SEARCH_PER_COUNTRY[country] ? (
                <Flex flex={1} mx={4}>
                  <Search />
                </Flex>
              ) : undefined
            }
            topMenuLinks={[Page.Home, Page.Orders]
              .concat(QUOTES_PAGE_PER_COUNTRY[country] ? Page.Quotes : [])
              .concat(Page.Products)
              .concat(
                INVOICES_PAGE_PER_COUNTRY[country] &&
                  isAllowedToSeePage(Page.Invoices, user)
                  ? Page.Invoices
                  : [],
              )
              .concat(
                EXTERNAL_USER_ADMIN_PER_COUNTRY[country] &&
                  isAllowedToSeePage(Page.Users, user)
                  ? Page.Users
                  : [],
              )
              .map(page => (
                <RouterLink key={page} isNav to={{ type: page }}>
                  {t(`page.${page}` as TranslationKey)}
                </RouterLink>
              ))}
          />
        </>
      )}
      <Flex direction="column" flexGrow={1} minH="100%">
        <Sentry.ErrorBoundary fallback={ErrorBoundary}>
          <UniversalComponent country={country} page={page} path="page" />
        </Sentry.ErrorBoundary>
      </Flex>
      <Footer />
      <Toaster alignHorizontal="left" onFinishedToasting={removeToast} />
      <ConfirmLogoutModal
        isOpen={!!confirmLogoutModalMessage}
        onClose={() => setConfirmLogoutModalMessage('')}
        onLogout={onLogout}
      >
        {confirmLogoutModalMessage}
      </ConfirmLogoutModal>
      <NavigationModal />
      <CartModalsController />
    </>
  )
}

export default App
